import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { Input, Button, Tooltip } from '@heroui/react';
import { useTranslations } from '../../../contexts/translation.context';
import { Typography } from '../../../components';
import { FilterSelect, DateFilterSelect, ModelSearchSelect, FilterOption } from '../../../components/ui';
import {
  SearchInputIc,
  ResetFiltersIc,
  AccountIcon,
  CalendarIcon,
} from '../../../components/icons';
import { RevshareBillsTable } from './components/RevshareBillsTable';
import { usePageBreadcrumbs } from '../../../hooks/usePageBreadcrumbs';
import { refShareApi, RefShareCost, GetRefShareCostsParams, CostStatus } from '../../../api/services/refShareApi';
import { accountsApi } from '../../../api/services/accountsApi';
import { showError } from '../../../utils/toast';
import { parseApiError } from '../../../utils/errorParser';

// Filters interface
interface BillsFilters {
  search: string;
  model_id: string | null;
  status: string;
  dateRange: string;
  customStartDate: string | null;
  customEndDate: string | null;
}

// Model for filter
interface AvailableModel {
  id: string;
  name: string;
  username: string;
  avatar?: string;
}

const DEFAULT_FILTERS: BillsFilters = {
  search: '',
  model_id: null,
  status: 'all',
  dateRange: 'all',
  customStartDate: null,
  customEndDate: null,
};

const DEFAULT_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_DELAY = 500;

// Format date as YYYY-MM-DD in local timezone
const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to get date range based on filter
const getDateRange = (dateRange: string, customStartDate?: string, customEndDate?: string) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (dateRange === 'custom' && customStartDate && customEndDate) {
    return {
      startDate: customStartDate,
      endDate: customEndDate
    };
  }

  switch (dateRange) {
    case 'today':
      return {
        startDate: formatLocalDate(today),
        endDate: formatLocalDate(today)
      };
    case 'week': {
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - 7);
      return {
        startDate: formatLocalDate(weekStart),
        endDate: formatLocalDate(today)
      };
    }
    case 'month': {
      const monthStart = new Date(today);
      monthStart.setDate(today.getDate() - 30);
      return {
        startDate: formatLocalDate(monthStart),
        endDate: formatLocalDate(today)
      };
    }
    case 'quarter': {
      const quarterStart = new Date(today);
      quarterStart.setDate(today.getDate() - 90);
      return {
        startDate: formatLocalDate(quarterStart),
        endDate: formatLocalDate(today)
      };
    }
    case 'halfyear': {
      const halfYearStart = new Date(today);
      halfYearStart.setDate(today.getDate() - 180);
      return {
        startDate: formatLocalDate(halfYearStart),
        endDate: formatLocalDate(today)
      };
    }
    case 'year': {
      const yearStart = new Date(today);
      yearStart.setDate(today.getDate() - 365);
      return {
        startDate: formatLocalDate(yearStart),
        endDate: formatLocalDate(today)
      };
    }
    case 'all':
    default:
      return {
        startDate: null,
        endDate: null
      };
  }
};

const RevshareBillsPage: React.FC = () => {
  const { t } = useTranslations();

  const breadcrumbs = useMemo(() => [
    { label: t('sidebar.dashboard'), href: '/dashboard' },
    { label: t('sidebar.revshareBills'), isActive: true },
  ], [t]);

  usePageBreadcrumbs(breadcrumbs);

  // Data state
  const [bills, setBills] = useState<RefShareCost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  // Filters state
  const [filters, setFilters] = useState<BillsFilters>(DEFAULT_FILTERS);
  const [localSearch, setLocalSearch] = useState('');
  const prevSearchRef = useRef(localSearch);

  // Models for filter
  const [availableModels, setAvailableModels] = useState<AvailableModel[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);

  // Pagination state
  const [sortBy, setSortBy] = useState<string>('created_at'); // created_at = newest first
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Status options
  const statusOptions = useMemo(() => [
    { key: 'all', label: t('admin.revshareBills.filters.status.all') },
    { key: 'in_pending', label: t('admin.revshareBills.filters.status.in_pending') },
    { key: 'paid', label: t('admin.revshareBills.filters.status.paid') },
  ], [t]);

  // Check if there are any active filters
  const hasActiveFilters = useMemo(() => {
    return (
      filters.search !== '' ||
      filters.model_id !== null ||
      filters.status !== 'all' ||
      filters.dateRange !== 'all' ||
      filters.customStartDate !== null ||
      filters.customEndDate !== null
    );
  }, [filters]);

  // Load available models for filter
  const fetchAvailableModels = useCallback(async (search: string = '') => {
    setModelsLoading(true);
    try {
      const response = await accountsApi.getOnlyFansAccounts({
        page: 1,
        page_size: 50,
        search: search
      });
      if (response && response.objects) {
        setAvailableModels(response.objects.map((acc: any) => ({
          id: acc._id || acc.id,
          name: acc.name || acc.of_user_name || '',
          username: acc.of_user_name || '',
          avatar: acc.avatar
        })));
      }
    } catch (error) {
      console.error('Error loading models:', error);
    } finally {
      setModelsLoading(false);
    }
  }, []);

  // Load models on mount
  useEffect(() => {
    fetchAvailableModels();
  }, [fetchAvailableModels]);

  const handleModelSearchChange = useCallback((value: string) => {
    fetchAvailableModels(value);
  }, [fetchAvailableModels]);

  // Fetch bills from API
  const fetchBills = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: GetRefShareCostsParams = {
        page: currentPage,
        page_size: pageSize,
        sort_by: sortBy as GetRefShareCostsParams['sort_by'],
      };

      // Search
      if (filters.search) {
        params.search = filters.search;
      }

      // Status filter
      if (filters.status && filters.status !== 'all') {
        params.status = filters.status as CostStatus;
      }

      // Model filter
      if (filters.model_id) {
        params.model_id = filters.model_id;
      }

      // Date range filter
      const dateRange = getDateRange(filters.dateRange, filters.customStartDate || undefined, filters.customEndDate || undefined);
      if (dateRange.startDate) params.created_at__start = dateRange.startDate;
      if (dateRange.endDate) params.created_at__end = dateRange.endDate;

      const response = await refShareApi.getCosts(params);

      setBills(response.objects || []);
      setTotalCount(response.count || 0);
    } catch (error) {
      console.error('Error loading revshare bills:', error);
      showError(parseApiError(error));
      setBills([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, filters, sortBy]);

  useEffect(() => {
    fetchBills();
  }, [fetchBills]);

  // Debounce search
  useEffect(() => {
    if (prevSearchRef.current === localSearch) return;

    const timeoutId = setTimeout(() => {
      prevSearchRef.current = localSearch;
      setFilters(prev => ({ ...prev, search: localSearch }));
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timeoutId);
  }, [localSearch]);

  const handleFilterChange = useCallback((key: keyof BillsFilters, value: string | null) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  }, []);

  const handleDateRangeChange = useCallback((value: string) => {
    setFilters(prev => ({
      ...prev,
      dateRange: value,
      customStartDate: value !== 'custom' ? null : prev.customStartDate,
      customEndDate: value !== 'custom' ? null : prev.customEndDate,
    }));
    setCurrentPage(1);
  }, []);

  const handleCustomDateChange = useCallback((startDate: string, endDate: string) => {
    setFilters(prev => ({
      ...prev,
      customStartDate: startDate,
      customEndDate: endDate,
      dateRange: 'custom',
    }));
    setCurrentPage(1);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setLocalSearch('');
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const handleSortChange = useCallback((newSortBy: string) => {
    setSortBy(newSortBy);
    setCurrentPage(1);
  }, []);

  // Model options for select
  const modelOptions: FilterOption[] = useMemo(() => {
    return availableModels.map((model) => ({
      key: model.id,
      avatar: model.avatar,
      username: model.username,
      name: model.name,
      label: model.name ? `${model.name} (@${model.username})` : `@${model.username}`
    }));
  }, [availableModels]);

  return (
    <div className="h-screen flex flex-col">
      <div className="flex-1">
        <div className="px-[24px] py-[24px]">
          <div className="mb-[16px]">
            <Typography variant="h5" className="text-[#11181C] font-semibold mb-[16px]">
              {t('admin.revshareBills.title')}
            </Typography>

            <div className="flex items-center gap-[16px] flex-wrap">
              {/* Search input */}
              <Input
                type="text"
                placeholder="Поиск по ID счета"
                size="sm"
                value={localSearch}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocalSearch(e.target.value)}
                endContent={<SearchInputIc />}
                classNames={{
                  mainWrapper: "w-[200px]",
                  base: "w-[200px]",
                  input: "text-sm text-[#11181C] placeholder:text-[#72727B]",
                  inputWrapper: "rounded-[8px] h-[36px] w-[200px] bg-[#F4F4F5] border-none shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]",
                  innerWrapper: "bg-transparent",
                }}
              />

              {/* Date filter */}
              <DateFilterSelect
                placeholder={t('admin.revshareBills.filters.datePlaceholder')}
                selectedKey={filters.dateRange}
                onSelectionChange={handleDateRangeChange}
                onCustomDateChange={handleCustomDateChange}
                customStartDate={filters.customStartDate || undefined}
                customEndDate={filters.customEndDate || undefined}
                variant="campaign"
                startContent={<CalendarIcon className="text-[#A8A8B0]" size={20} />}
              />

              {/* Model filter */}
              <ModelSearchSelect
                placeholder={t('admin.revshareBills.filters.account.placeholder')}
                options={modelOptions}
                selectedKey={filters.model_id || 'all'}
                onSelectionChange={(value) => {
                  const selected = value === 'all' ? null : value;
                  handleFilterChange('model_id', selected);
                }}
                onSearchChange={handleModelSearchChange}
                isLoading={modelsLoading}
                startContent={<AccountIcon className="text-[#A8A8B0] stroke-[#A8A8B0]" size={20} />}
                variant="campaign"
                autoWidth={true}
              />

              {/* Status filter */}
              <FilterSelect
                placeholder={t('admin.revshareBills.filters.status.placeholder')}
                options={statusOptions}
                selectedKey={filters.status}
                variant="campaign"
                onSelectionChange={(value) => handleFilterChange('status', value)}
                startContent={
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M4.72458 8.26132L7.27637 4.80879C8.92584 2.57708 9.75057 1.46122 10.52 1.69703C11.2895 1.93283 11.2895 3.30142 11.2895 6.03861V6.2967C11.2895 7.28394 11.2895 7.77757 11.605 8.08719L11.6216 8.10323C11.9439 8.40632 12.4577 8.40632 13.4852 8.40632C15.3342 8.40632 16.2588 8.40632 16.5712 8.9671C16.5764 8.97639 16.5814 8.98575 16.5863 8.99518C16.8813 9.56465 16.346 10.2889 15.2753 11.7374L12.7236 15.1899C11.0741 17.4216 10.2493 18.5375 9.47985 18.3017C8.71038 18.0659 8.7104 16.6972 8.71044 13.96L8.71044 13.7021C8.71046 12.7148 8.71046 12.2212 8.39501 11.9115L8.37832 11.8955C8.05607 11.5924 7.5423 11.5924 6.51477 11.5924C4.66571 11.5924 3.74119 11.5924 3.42873 11.0316C3.42355 11.0223 3.41852 11.013 3.41364 11.0036C3.11868 10.4341 3.65398 9.70983 4.72458 8.26132Z" stroke="#A8A8B0" strokeWidth="1.5"/>
                  </svg>
                }
                autoWidth={true}
              />

              {/* Reset filters button */}
              {hasActiveFilters && (
                <Tooltip content={t('admin.revshareBills.filters.resetTooltip')} placement="top">
                  <Button
                    size="sm"
                    className="h-[36px] rounded-[8px] !w-[36px] p-0 bg-[#F4F4F5] min-w-[36px]"
                    onPress={handleResetFilters}
                  >
                    <ResetFiltersIc />
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>

          <RevshareBillsTable
            bills={bills}
            pagination={{
              count: totalCount,
              page_size: pageSize,
              page: currentPage,
            }}
            isLoading={isLoading}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            sortBy={sortBy}
            onSortChange={handleSortChange}
          />
        </div>
      </div>
    </div>
  );
};

export default RevshareBillsPage;
